import {
  ShieldCheck,
  Building2,
  Briefcase,
  Calendar,
  Settings,
  ClipboardList,
  DollarSign,
  Bell,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent } from '@zaogig/ui/card';
import { Badge } from '@zaogig/ui/badge';
import { Button } from '@zaogig/ui/button';

type ColorKey = 'blue' | 'purple' | 'red' | 'green' | 'orange' | 'gray';

interface AdminSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  color: ColorKey;
  badge: number;
}

// 运营入口配置
const adminSections: AdminSection[] = [
  {
    id: 'enterprise-review',
    title: '企业审核',
    icon: <Building2 className="h-6 w-6" />,
    description: '审核企业入驻申请、企业信息变更',
    color: 'blue',
    badge: 12,
  },
  {
    id: 'job-review',
    title: '岗位审核',
    icon: <Briefcase className="h-6 w-6" />,
    description: '审核岗位发布、岗位内容合规性',
    color: 'purple',
    badge: 28,
  },
  {
    id: 'risk-events',
    title: '风控事件',
    icon: <ShieldCheck className="h-6 w-6" />,
    description: '监控和处理风控告警、异常行为',
    color: 'red',
    badge: 5,
  },
  {
    id: 'evidence-chain',
    title: '证据链查看',
    icon: <FileText className="h-6 w-6" />,
    description: '查看打卡证据链、纠纷证据',
    color: 'gray',
    badge: 0,
  },
  {
    id: 'schedule-monitor',
    title: '排班监控',
    icon: <Calendar className="h-6 w-6" />,
    description: '监控实时排班、到岗情况',
    color: 'green',
    badge: 3,
  },
  {
    id: 'settlement-review',
    title: '结算复核',
    icon: <DollarSign className="h-6 w-6" />,
    description: '复核结算单、处理结算异议',
    color: 'orange',
    badge: 8,
  },
  {
    id: 'system-config',
    title: '系统配置',
    icon: <Settings className="h-6 w-6" />,
    description: '平台参数、规则配置',
    color: 'gray',
    badge: 0,
  },
];

// 快速统计数据
const quickStats = [
  { label: '待处理企业', value: '12', color: 'blue' },
  { label: '待审核岗位', value: '28', color: 'purple' },
  { label: '风控告警', value: '5', color: 'red' },
  { label: '待结算单', value: '8', color: 'orange' },
];

// 最近告警数据
const recentAlerts = [
  { id: '1', type: 'risk', title: '同一设备多账号登录', time: '2分钟前', level: 'high' },
  { id: '2', type: 'attendance', title: '打卡地点超出围栏', time: '15分钟前', level: 'medium' },
  { id: '3', type: 'settlement', title: '结算金额异常', time: '1小时前', level: 'low' },
];

const colorClasses: Record<ColorKey, string> = {
  blue: 'bg-blue-50 text-blue-600',
  purple: 'bg-purple-50 text-purple-600',
  red: 'bg-red-50 text-red-600',
  green: 'bg-green-50 text-green-600',
  orange: 'bg-orange-50 text-orange-600',
  gray: 'bg-gray-100 text-gray-600',
};

export default function AdminConsole() {
  const handleSectionClick = (id: string) => {
    console.log('点击管理模块:', id);
  };

  return (
    <div className="wx-page bg-gray-50">
      {/* 头部区域 */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 px-4 pt-8 pb-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400">运营管理</p>
            <h1 className="text-xl font-bold text-white">平台后台</h1>
          </div>
          <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white">
            <Bell className="h-4 w-4" />
          </Button>
        </div>

        {/* 快速统计 */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          {quickStats.map((stat, index) => (
            <Card key={index} className="bg-white/10 border-white/20">
              <CardContent className="p-4">
                <p className={`text-2xl font-bold text-white`}>{stat.value}</p>
                <p className="mt-1 text-xs text-gray-400">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* 运营入口区域 */}
      <section className="px-4 pt-5">
        <div className="mb-3 flex items-center gap-2 text-gray-900">
          <ClipboardList className="h-4 w-4" />
          <h2 className="font-semibold">运营管理</h2>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {adminSections.map((section) => (
            <Card 
              key={section.id} 
              className="wx-card cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => handleSectionClick(section.id)}
            >
              <CardContent className="p-4">
                <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${colorClasses[section.color]}`}>
                  {section.icon}
                </div>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 text-sm">{section.title}</h3>
                  {section.badge > 0 && (
                    <Badge className="bg-red-500">{section.badge}</Badge>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500 line-clamp-2">{section.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* 最近告警区域 */}
      <section className="px-4 pt-5 pb-8">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-900">
            <AlertTriangle className="h-4 w-4" />
            <h2 className="font-semibold">最近告警</h2>
          </div>
          <Button variant="ghost" size="sm" className="text-gray-500 text-xs">
            查看全部
          </Button>
        </div>
        <div className="space-y-3">
          {recentAlerts.map((alert) => (
            <Card key={alert.id} className="wx-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 h-2 w-2 rounded-full ${
                      alert.level === 'high' ? 'bg-red-500' : 
                      alert.level === 'medium' ? 'bg-orange-500' : 'bg-yellow-500'
                    }`} />
                    <div>
                      <h3 className="font-medium text-gray-900 text-sm">{alert.title}</h3>
                      <p className="mt-1 text-xs text-gray-500">{alert.time}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={
                    alert.level === 'high' ? 'border-red-200 text-red-600' :
                    alert.level === 'medium' ? 'border-orange-200 text-orange-600' :
                    'border-yellow-200 text-yellow-600'
                  }>
                    {alert.level === 'high' ? '高危' : alert.level === 'medium' ? '中危' : '低危'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
